import React from 'react';
import {
    ConsiderationDetail,
    ConsiderationTermBase,
    DeferredVariablePaymentTerm,
    NegotiatedTerm,
} from '../../../models/FlowOfFunds/flow-of-funds.model';
import { TypeOfConsideration } from '../../../slices/marketplace/pricing/models/type-of-consideration.enum';
import {
    EM_DASH,
    PUT_CALL_LABELS,
    considerationTypeName,
    formatMoney,
    formatMultiple,
    formatPercent,
} from '../../../utils/flowOfFundsFormat';
import { ExpandedText } from '../../../containers/DataRequest/ExpandedText/ExpandedText';

const COMMENT_CLAMP_LINES = 3;
const COMMENT_HEIGHT_PX = 54;

const Fixed = ({ value }: { value: string }) => (
    <td>
        <span className="ff-detail__fixed">{value || EM_DASH}</span>
    </td>
);

const CommentCell = ({ value, uniqueId }: { value: string | null; uniqueId: string }) => (
    <td>
        {value ? (
            <ExpandedText
                content={value}
                uniqueId={uniqueId}
                heightToShowLinksPx={COMMENT_HEIGHT_PX}
                clampLines={COMMENT_CLAMP_LINES}
                className="ff-detail__comment"
            />
        ) : (
            <span className="ff-detail__fixed">{EM_DASH}</span>
        )}
    </td>
);

const yesNo = (value: boolean) => (value ? 'Yes' : 'No');
const negotiatedDays = (term: NegotiatedTerm) =>
    term.currentValue == null ? '' : String(term.currentValue);
const employmentTailLabel = (months: number | null) => (months == null ? '' : `${months} months`);

const TAIL_HEADERS = [
    'Admin Comment',
    'Buyer Comment',
    'Buyer Calculation Period, calendar days from payment timing',
    'Seller Calculation Response Period, calendar days',
    'Payment Date, calendar days from calculation agreement',
    'Put/Call Right',
];

const headersFor = (leading: string[]) => [
    ...leading,
    ...TAIL_HEADERS,
];

interface TermTailProps {
    idPrefix: string;
    term: ConsiderationTermBase;
}

const TermTail = ({ idPrefix, term }: TermTailProps) => (
    <>
        <CommentCell value={term.adminComment} uniqueId={`${idPrefix}-admin-comment-${term.id}`} />
        <CommentCell value={term.buyerComment} uniqueId={`${idPrefix}-buyer-comment-${term.id}`} />
        <Fixed value={negotiatedDays(term.buyerCalculationPeriodDays)} />
        <Fixed value={negotiatedDays(term.sellerResponsePeriodDays)} />
        <Fixed value={negotiatedDays(term.paymentDateOffsetDays)} />
        <Fixed value={PUT_CALL_LABELS[term.putCallRight]} />
    </>
);

const DetailTable = ({
    title,
    headers,
    children,
}: {
    title: string;
    headers: string[];
    children: React.ReactNode;
}) => (
    <>
        <div className="ff-detail__group-title">{title}</div>
        <div className="ff-detail__scroll">
            <table className="ff-detail__table">
                <thead>
                    <tr>
                        {headers.map((header, index) => (
                            <th key={`${header}-${index}`}>{header}</th>
                        ))}
                    </tr>
                </thead>
                <tbody>{children}</tbody>
            </table>
        </div>
    </>
);

const TotalRow = ({ leadingBlanks, total }: { leadingBlanks: number; total: number }) => (
    <tr className="ff-detail__total-row">
        <td>Total</td>
        {Array.from({ length: leadingBlanks }).map((_, index) => (
            <td key={index} />
        ))}
        <td>{formatMoney(total)}</td>
    </tr>
);

const DFP_LEADING_HEADERS = [
    '',
    'Payment Timing',
    'Amount',
    'Annualized Interest Accrual Rate',
    'Contingent?',
    'Contingency Dependent Variable (“ContDV”)',
    'ContDV minimum value',
    'Employment Contingency',
    'Tail Period for Employment',
];

const DVP_LEADING_HEADERS = [
    '',
    'Payment Timing',
    'Type',
    'Value at Closing',
    'Calculation Dependent Variable (“CalcDV”)',
    'CalcDV Baseline Value',
    'Calculation Multiple',
    'Maximum Payment',
    'Employment Contingency',
    'Tail period for Employment Contingency',
    'Penalty Multiple',
    'Distribution Periodicity',
];

const renderDvpRow = (
    term: DeferredVariablePaymentTerm,
    idPrefix: string
) => (
    <tr key={term.id}>
        <td className="ff-detail__row-label">{term.label}</td>
        <Fixed value={term.paymentTiming} />
        <Fixed value={considerationTypeName(term.considerationType)} />
        <Fixed value={formatMoney(term.valueAtClosing)} />
        <Fixed value={term.calculationDependentVariable ?? ''} />
        <Fixed value={formatMoney(term.calcDvBaselineValue)} />
        <Fixed value={formatMultiple(term.calculationMultiple)} />
        <Fixed value={formatMoney(term.maximumPayment)} />
        <Fixed value={yesNo(term.hasEmploymentContingency)} />
        <Fixed value={employmentTailLabel(term.employmentTailPeriodMonths)} />
        <Fixed value={formatMultiple(term.penaltyMultiple)} />
        <Fixed value={term.distributionPeriodicity ?? ''} />
        <TermTail idPrefix={idPrefix} term={term} />
    </tr>
);

interface ConsiderationDetailTablesProps {
    detail: ConsiderationDetail;
}

export const ConsiderationDetailTables = ({ detail }: ConsiderationDetailTablesProps) => {
    const { deferredFixedPayments, deferredVariablePayments } = detail;
    const jv = detail.jvEquity;
    const common = detail.commonCorporateEquity;
    const preferred = detail.preferredCorporateEquity;

    const earnoutTerms = deferredVariablePayments.filter(
        (term) => term.considerationType === TypeOfConsideration.Earnout
    );
    const syntheticTerms = deferredVariablePayments.filter(
        (term) => term.considerationType === TypeOfConsideration.SyntheticJvEquity
    );

    const fixedTotal = deferredFixedPayments.reduce((sum, term) => sum + term.amount, 0);
    const earnoutTotal = earnoutTerms.reduce((sum, term) => sum + (term.valueAtClosing ?? 0), 0);
    const syntheticTotal = syntheticTerms.reduce((sum, term) => sum + (term.valueAtClosing ?? 0), 0);

    const hasAnyTerms =
        deferredFixedPayments.length > 0 ||
        earnoutTerms.length > 0 ||
        syntheticTerms.length > 0 ||
        !!jv ||
        !!common ||
        !!preferred;

    if (!hasAnyTerms) {
        return null;
    }

    return (
        <div className="ff-detail">
            <h4 className="ff-detail__title">Consideration Detail</h4>

            {/* ── Deferred Fixed Payments ── */}
            {deferredFixedPayments.length > 0 && (
                <DetailTable
                    title="Deferred Fixed Payments"
                    headers={headersFor(DFP_LEADING_HEADERS)}>
                    {deferredFixedPayments.map((term) => (
                        <tr key={term.id}>
                            <td className="ff-detail__row-label">{term.label}</td>
                            <Fixed value={term.paymentTiming} />
                            <Fixed value={formatMoney(term.amount)} />
                            <Fixed value={formatPercent(term.annualInterestAccrualRate)} />
                            <Fixed value={yesNo(term.isContingent)} />
                            <Fixed value={term.contingencyDependentVariable ?? ''} />
                            <Fixed value={formatMoney(term.contingencyMinimumValue)} />
                            <Fixed value={yesNo(term.hasEmploymentContingency)} />
                            <Fixed value={employmentTailLabel(term.employmentTailPeriodMonths)} />
                            <TermTail
                                idPrefix="dfp"
                                term={term}
                            />
                        </tr>
                    ))}
                    <TotalRow leadingBlanks={1} total={fixedTotal} />
                </DetailTable>
            )}

            {/* ── Earnout ── */}
            {earnoutTerms.length > 0 && (
                <DetailTable
                    title="Earnout"
                    headers={headersFor(DVP_LEADING_HEADERS)}>
                    {earnoutTerms.map((term) =>
                        renderDvpRow(term, 'earnout')
                    )}
                    <TotalRow leadingBlanks={2} total={earnoutTotal} />
                </DetailTable>
            )}

            {/* ── Synthetic JV Equity ── */}
            {syntheticTerms.length > 0 && (
                <DetailTable
                    title="Synthetic JV Equity"
                    headers={headersFor(DVP_LEADING_HEADERS)}>
                    {syntheticTerms.map((term) =>
                        renderDvpRow(term, 'synthetic')
                    )}
                    <TotalRow leadingBlanks={2} total={syntheticTotal} />
                </DetailTable>
            )}

            {/* ── JV Equity ── */}
            {jv && (
                <DetailTable
                    title="JV Equity"
                    headers={headersFor([
                        'Value at Closing',
                        'Payment Timing',
                        'Calculation Dependent Variable (“CalcDV”)',
                        'CalcDV Baseline Value',
                        'Calculation Multiple',
                        'Employment Contingency',
                        'Tail period for Employment Contingency',
                        'Penalty Multiple',
                        'Distribution Periodicity',
                    ])}>
                    <tr>
                        <Fixed value={formatMoney(jv.valueAtClosing)} />
                        <Fixed value={jv.paymentTiming} />
                        <Fixed value={jv.calculationDependentVariable ?? ''} />
                        <Fixed value={formatMoney(jv.calcDvBaselineValue)} />
                        <Fixed value={formatMultiple(jv.calculationMultiple)} />
                        <Fixed value={yesNo(jv.hasEmploymentContingency)} />
                        <Fixed value={employmentTailLabel(jv.employmentTailPeriodMonths)} />
                        <Fixed value={formatMultiple(jv.penaltyMultiple)} />
                        <Fixed value={jv.distributionPeriodicity ?? ''} />
                        <TermTail idPrefix="jv" term={jv} />
                    </tr>
                </DetailTable>
            )}

            {/* ── Common Corp Equity ── */}
            {common && (
                <DetailTable
                    title="Common Corp Equity"
                    headers={headersFor([
                        'Value at Closing',
                        'Expected Timing to Recap',
                        'Expected Payout, as multiple of current value',
                    ])}>
                    <tr>
                        <Fixed value={formatMoney(common.valueAtClosing)} />
                        <Fixed value={common.expectedTimingToRecap} />
                        <Fixed value={formatMultiple(common.expectedPayoutMultiple)} />
                        <TermTail
                            idPrefix="common"
                            term={common}
                        />
                    </tr>
                </DetailTable>
            )}

            {/* ── Preferred Corp Equity ── */}
            {preferred && (
                <DetailTable
                    title="Preferred Corp Equity"
                    headers={headersFor([
                        'Value at Closing',
                        'Annual Dividend Rate',
                        'Dividend Payment Timing',
                        'Compounding Frequency',
                        'Expected Timing to Recap',
                        'Convertible into Common',
                        'Conversion Ratio',
                    ])}>
                    <tr>
                        <Fixed value={formatMoney(preferred.valueAtClosing)} />
                        <Fixed value={formatPercent(preferred.annualDividendRate)} />
                        <Fixed value={preferred.dividendPaymentTiming ?? ''} />
                        <Fixed value={preferred.compoundingFrequency ?? ''} />
                        <Fixed value={preferred.expectedTimingToRecap} />
                        <Fixed value={yesNo(preferred.isConvertibleIntoCommon)} />
                        <Fixed
                            value={
                                preferred.conversionRatio == null
                                    ? ''
                                    : `${preferred.conversionRatio} : 1`
                            }
                        />
                        <TermTail
                            idPrefix="preferred"
                            term={preferred}
                        />
                    </tr>
                </DetailTable>
            )}
        </div>
    );
};
